/**
 * One answer to "may this page move?", which every motion system asks.
 *
 * The system preference is the default and it is honoured. What it is not is the final
 * word: `prefers-reduced-motion` is set once, for an entire operating system, and it
 * cannot know that on this page it is the difference between a museum and a slideshow.
 * Someone who turned animations off to stop menus sliding still gets a site with no
 * video in it and no indication that video exists — which is what happened here.
 *
 * So the preference seeds the default and a control on the page overrides it, in either
 * direction. Someone with no system preference can also turn motion off.
 *
 * The answer is published as an attribute on the document element so CSS can read it
 * too. A `@media (prefers-reduced-motion: reduce)` block cannot be overridden by a
 * choice made on the page, which is why the stylesheet keys off the attribute instead.
 */

const STORAGE_KEY = "simam-heritage-motion";

export function systemPrefersStillness(): boolean {
  return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

function stored(): boolean | null {
  try {
    const value = window.localStorage.getItem(STORAGE_KEY);
    if (value === "on") return true;
    if (value === "off") return false;
    return null;
  } catch {
    return null;
  }
}

/**
 * Whether this page may animate: an explicit choice if there is one, else on.
 *
 * Motion is the default even when the system asks for stillness. That is a deliberate
 * departure, made because this page is the motion — a visitor arriving on the still
 * version sees photographs and has no way to know there is anything else, which is
 * worse than an unexpected animation. The control is always present and always visible,
 * so turning it off is one click and the preference then sticks.
 */
export function motionAllowed(): boolean {
  return stored() ?? true;
}

/** True when the visitor is overriding a system preference for stillness. */
export function motionIsOverride(): boolean {
  return stored() === true && systemPrefersStillness();
}

export function setMotion(allowed: boolean): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, allowed ? "on" : "off");
  } catch {
    // A locked-down kiosk still gets the choice for this visit.
  }
  applyMotion();
}

/** Publish the current answer for the stylesheet. Call before the first paint. */
export function applyMotion(): void {
  document.documentElement.dataset.motion = motionAllowed() ? "on" : "off";
}
